import { Calendar } from 'lucide-react';
import { useAcademicCyclesStore, academicCyclesSelectors } from '@/shared/stores/academic-cycles.store';
import { Tooltip } from '@/shared/components/ui/tooltip';

interface ActiveCycleIndicatorProps {
  /**
   * Display variant
   * - 'sidebar': Full-width horizontal layout for sidebar (desktop)
   * - 'compact': Minimal badge for mobile header
   */
  variant?: 'sidebar' | 'compact';

  /**
   * Optional workspace ID to filter cycle
   */
  workspaceId?: number;

  /**
   * Whether sidebar is collapsed (for sidebar variant)
   */
  isCollapsed?: boolean;
}

/**
 * ActiveCycleIndicator
 *
 * Displays the currently active academic cycle across the application.
 * Uses the Zustand store to fetch the current cycle within the date range.
 *
 * Features:
 * - Shows active cycle name with visual indicator
 * - Gracefully handles no active cycle scenario
 * - Supports sidebar and compact (mobile) variants
 * - Full dark mode support
 * - Matches brand violet accent color (uses --brand-primary CSS variable)
 *
 * Usage:
 * ```tsx
 * // In Sidebar (full width)
 * <ActiveCycleIndicator variant="sidebar" />
 *
 * // In Mobile Header (compact)
 * <ActiveCycleIndicator variant="compact" />
 * ```
 */
export function ActiveCycleIndicator({
  variant = 'sidebar',
  workspaceId,
  isCollapsed = false
}: ActiveCycleIndicatorProps) {
  const currentCycle = useAcademicCyclesStore(
    academicCyclesSelectors.selectCurrentCycle(workspaceId)
  );

  // Compact variant for mobile
  if (variant === 'compact') {
    if (!currentCycle) {
      return (
        <div
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-800"
          title="No hay ciclo activo"
        >
          <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">-</span>
        </div>
      );
    }

    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-violet-50 dark:bg-violet-950/30"
        title={`Ciclo activo: ${currentCycle.name}`}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse" />
        <span className="text-xs text-[var(--brand-primary)] dark:text-violet-300 font-semibold truncate max-w-[100px]">
          {currentCycle.name}
        </span>
      </div>
    );
  }

  // Sidebar variant (default)
  // Collapsed mode - Icon only with tooltip
  if (isCollapsed) {
    const tooltipContent = currentCycle
      ? `Ciclo activo: ${currentCycle.name}`
      : 'No hay ciclo activo';

    return (
      <div className="hidden lg:flex justify-center mx-4 mb-4">
        <Tooltip content={tooltipContent} side="right">
          <div
            className={`relative w-10 h-10 rounded-full flex items-center justify-center ${
              currentCycle
                ? 'bg-[var(--brand-primary)] shadow-lg shadow-purple-200 dark:shadow-purple-900/30'
                : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <Calendar className={`w-5 h-5 ${
              currentCycle
                ? 'text-white'
                : 'text-gray-400 dark:text-gray-500'
            }`} />
            {currentCycle && (
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-white dark:border-gray-900 animate-pulse" />
            )}
          </div>
        </Tooltip>
      </div>
    );
  }

  // Expanded mode - Full display
  if (!currentCycle) {
    return (
      <div className="mx-4 mb-4 p-3 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Ciclo Académico
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500 italic">
              No hay ciclo activo
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 mb-4 p-3 rounded-2xl bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800/50">
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-[var(--brand-primary)]/10 dark:bg-[var(--brand-primary)]/20">
          <Calendar className="w-4 h-4 text-[var(--brand-primary)] dark:text-violet-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">
            Ciclo Académico
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse" />
            <div className="text-sm font-semibold text-[var(--brand-primary)] dark:text-violet-300 truncate">
              {currentCycle.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
